import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { FaMoneyBillWave, FaServer, FaExclamationTriangle, FaSync, FaExpand } from "react-icons/fa";
import ScrollableTable from "../components/ScrollableTable";
import TenantTabs from "../components/TenantTabs";
import TimeFilter from "../components/TimeFilter";
import LoadingOverlay from "../components/LoadingOverlay";
import InfoTooltip from "../components/InfoTooltip";
import ExpandedChartModal from "../components/ExpandedChartModal";
import { fetchBillingOverview } from "../services/billingService";
import { getAvailablePlans } from "../services/analyticsService";
import { useTimeFilter } from "../context/TimeFilterContext";


/**
 * @file BillingAndSubscriptions.js
 * @description Page for detailed financial metrics, MRR, and per-tenant usage/billing analysis.
 */
export default function BillingAndSubscription() {
  const { timeFilter, setTimeFilter, customRange, handleCustomRangeChange, planFilter, setPlanFilter, availablePlans, setAvailablePlans } = useTimeFilter();
  const [activeTab, setActiveTab] = useState("estrel"); // estrel, education, enterprise
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [data, setData] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);

  const loadData = (force = false) => {
    // Guard clause
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;

    setLoading(true);
    // Fixed argument order: timePeriod, customRange, planFilter, forceRefresh
    // Note: billingService.js fetchBillingOverview signature is (timePeriod, customRange = null, planFilter = null)
    // but typically service functions don't take forceRefresh unless added.
    // Let's check billingService.js carefully. It does NOT seem to accept forceRefresh based on my previous view.
    // Wait, I need to check billingService again or assume standard signature.
    // Looking at previous Step 119 view of billingService.js:
    // export async function fetchBillingOverview(timePeriod, customRange = null, planFilter = null)
    // It does not accept 'force'.
    fetchBillingOverview(timeFilter, customRange, planFilter, force).then((res) => {
      // Sanitize Data for Charts
      if (res) {
        if (res.revenueTrend && res.revenueTrend.series) {
          res.revenueTrend.series = res.revenueTrend.series.map(s => ({
            ...s,
            data: s.data.map(v => Number(v) || 0)
          }));
        }
        if (res.planDistribution && res.planDistribution.series) {
          res.planDistribution.series = res.planDistribution.series.map(v => Number(v) || 0);
        }
      }
      setData(res);
      setLastUpdated(new Date());

      // Delay to allow charts to render
      setTimeout(() => {
        setLoading(false);
      }, 600);
    });
  };

  useEffect(() => {
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
    getAvailablePlans(timeFilter, customRange).then(setAvailablePlans);
  }, [timeFilter, customRange, setAvailablePlans]);

  useEffect(() => {
    loadData();
  }, [timeFilter, customRange, planFilter]); // Trigger on filter change

  const refreshData = () => {
    loadData(true);
  };

  const handleExpandChart = (title, options, series, type) => {
    setExpandedChart({ title, options, series, type });
  };

  if (!data && loading) {
    return (
      <div className="p-4 position-relative" style={{ minHeight: "100vh" }}>
        <LoadingOverlay loading={true} message="Loading Financial Data..." />
      </div>
    );
  }

  if (!data) return null;

  const kpis = {
    mrr: `$${data.metrics.totalMRR.toLocaleString()}`,
    avgUsage: "58%", // Derived or mocked in service
    risk: data.metrics.risk || "Low"
  };

  // Sort by Revenue Descending (or fallback to executions if revenue 0)
  const tenants = [...data.topTenants].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));

  // --- CHART OPTIONS ---
  const revenueOptions = {
    chart: { toolbar: { show: false }, type: "area", animations: { enabled: false } }, // Disable animations
    stroke: { curve: "smooth", width: 2 },
    colors: ["#AD2D45"],
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
    dataLabels: { enabled: false },
    xaxis: { categories: data.revenueTrend.labels },
    markers: { size: 4, hover: { size: 6 } }, // Ensure points are visible
  };

  const donutOptions = {
    chart: { type: "donut", animations: { enabled: false } }, // Disable animations
    labels: data.planDistribution.labels,
    colors: ["#E68F17", "#AD2D45", "#F4D677", "#912059"],
    legend: { position: "bottom" }
  };

  // --- COLOR SCALING HELPER ---
  const generateColorRanges = (values, baseColor) => {
    if (!values.length) return [];

    // Filter out Inifinity/NaN just in case
    const validValues = values.filter(v => Number.isFinite(v));
    if (!validValues.length) return [];

    const min = Math.min(...validValues);
    const max = Math.max(...validValues);
    const step = (max - min) / 4;

    // We want 4 buckets. Darker for higher values.
    // baseColor is expected to be a hex string. 
    // Simplified Palette logic:
    const palette = {
      "#912059": ["#F8D7E3", "#E08FB1", "#BC4C7D", "#912059"], // Execution (Pink/Red to Dark)
      "#E68F17": ["#FDE8C3", "#FCD085", "#F4B647", "#E68F17"]  // Storage (Yellow/Orange to Dark)
    };

    const colors = palette[baseColor] || ["#CFD8DC", "#90A4AE", "#546E7A", "#263238"];

    if (step === 0) {
      return [{ from: min, to: max + 1, color: colors[3] }];
    }

    return [
      { from: 0, to: Math.max(0, min + step), color: colors[0] },
      { from: min + step + 0.0001, to: min + (2 * step), color: colors[1] },
      { from: min + (2 * step) + 0.0001, to: min + (3 * step), color: colors[2] },
      { from: min + (3 * step) + 0.0001, to: max + 1, color: colors[3] }
    ];
  };

  // Prepare Series Data needed for scaling calculation
  const executionValues = tenants.map(t => Number(t.executions) || 0);
  const storageValues = tenants.map(t => parseFloat(String(t.storage || "0").replace(/[^\d.]/g, '') || "0"));

  const executionRanges = generateColorRanges(executionValues, "#912059");
  const storageRanges = generateColorRanges(storageValues, "#E68F17");

  // Treemap Options for Distribution Views
  const baseTreemapOptions = {
    chart: { toolbar: { show: false }, type: "treemap", animations: { enabled: false } },
    legend: { show: false },
    dataLabels: {
      enabled: true,
      style: { fontSize: "12px", fontWeight: "bold", colors: ["#000000"] }, // Dark text for lighter shades? Or adjust logic.
      formatter: function (text, op) {
        return [text, op.value];
      },
      offsetY: -4
    },
    plotOptions: {
      treemap: {
        distributed: true,
        enableShades: false,
        useFillColorAsStroke: true
      }
    }
  };

  const executionTreemapOptions = {
    ...baseTreemapOptions,
    plotOptions: {
      treemap: {
        ...baseTreemapOptions.plotOptions.treemap,
        colorScale: {
          ranges: executionRanges
        }
      }
    }
  };

  const storageTreemapOptions = {
    ...baseTreemapOptions,
    plotOptions: {
      treemap: {
        ...baseTreemapOptions.plotOptions.treemap,
        colorScale: {
          ranges: storageRanges
        }
      }
    }
  };

  const executionSeries = [{
    data: tenants.map(t => ({
      x: t.name,
      y: Number(t.executions) || 0
    }))
  }];

  const storageSeries = [{
    data: tenants.map(t => ({
      x: t.name,
      y: parseFloat(String(t.storage || "0").replace(/[^\d.]/g, '') || "0")
    }))
  }];

  return (
    <div className="p-4 position-relative">
      <LoadingOverlay loading={loading} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-0">Financials & Usage</h2>
          <p className="text-muted mb-0">Is platform usage financially sustainable?</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          {availablePlans.length > 0 && (
            <select
              className="form-select form-select-sm shadow-sm border-secondary-subtle"
              style={{ width: "150px", fontWeight: "500" }}
              value={planFilter || ""}
              onChange={(e) => setPlanFilter(e.target.value || null)}
            >
              <option value="">All Plans</option>
              {availablePlans.map((p) => (
                <option key={p} value={p}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </option>
              ))}
            </select>
          )}
          <TimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            startDate={customRange.start}
            endDate={customRange.end}
            onCustomRangeChange={handleCustomRangeChange}
          />
          <div className="text-end d-none d-md-block ms-2">
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>Last Update</div>
            <div className="fw-bold small">{lastUpdated ? lastUpdated.toLocaleTimeString() : "-"}</div>
          </div>
          <button className="btn btn-white border shadow-sm btn-sm px-3" onClick={refreshData} disabled={loading}>
            <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
          </button>
        </div>
      </div>

      {/* TABS */}
      <TenantTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* KPIS */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 bg-success-subtle text-success rounded">
                <FaMoneyBillWave size={20} />
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Revenue <InfoTooltip text="Total  Revenue Generated" /></div>
                <h3 className="fw-bold mb-0">{kpis.mrr}</h3>
                <small className="text-muted">Revenue generated from Subscribers</small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 bg-warning-subtle text-warning rounded">
                <FaServer size={20} />
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Avg Usage / Subscriber <InfoTooltip text="Average resource utilization per Subscriber." /></div>
                <h3 className="fw-bold mb-0">{kpis.avgUsage}</h3>
                <small className="text-muted">Resource Consumption</small>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm p-3">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 bg-danger-subtle text-danger rounded">
                <FaExclamationTriangle size={20} />
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Risk Indicator <InfoTooltip text="Subscribers exceeding 80% of plan limits." /></div>
                <h3 className="fw-bold mb-0">{kpis.risk}</h3>
                <small className="text-danger">High Usage</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GRAPHS ROW 1 */}
      <div className="row g-4 mb-4">
        <div className={`${activeTab === "estrel" ? "col-lg-8" : "col-12"}`}>
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Revenue Trend <InfoTooltip text="Revenue growth over time." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Revenue Trend", revenueOptions, data.revenueTrend.series, "area")}>
                <FaExpand />
              </button>
            </div>
            {data.revenueTrend.series[0].data.length > 0 ? (
              <Chart
                options={revenueOptions}
                series={data.revenueTrend.series}
                type="area"
                height={300}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "300px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>

        {/* Donut only for Estrel SaaS */}
        {activeTab === "estrel" && (
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm p-4 h-100">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0 text-uppercase">Revenue by Plan <InfoTooltip text="Revenue breakdown by subscription tier." /></h6>
                <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Revenue by Plan", donutOptions, data.planDistribution.series, "donut")}>
                  <FaExpand />
                </button>
              </div>
              {data.planDistribution.series.length > 0 && data.planDistribution.series.some(v => v > 0) ? (
                <Chart
                  options={donutOptions}
                  series={data.planDistribution.series}
                  type="donut"
                  height={300}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "300px" }}>
                  No data to show
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* GRAPHS ROW 2: Treemaps */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Execution Volume Distribution <InfoTooltip text="Visualize the distribution of number of executions across subscribers. Darker shade = Higher volume." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Execution Volume Distribution", executionTreemapOptions, executionSeries, "treemap")}>
                <FaExpand />
              </button>
            </div>
            {executionSeries[0].data.some(d => d.y > 0) ? (
              <Chart
                options={executionTreemapOptions}
                series={executionSeries}
                type="treemap"
                height={280}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "280px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-uppercase">Storage Consumption Map <InfoTooltip text="Visualize the distribution of storage usage (MB) across subscribers. Darker shade = Higher usage." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Storage Consumption Map", storageTreemapOptions, storageSeries, "treemap")}>
                <FaExpand />
              </button>
            </div>
            {storageSeries[0].data.some(d => d.y > 0) ? (
              <Chart
                options={storageTreemapOptions}
                series={storageSeries}
                type="treemap"
                height={280}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "280px" }}>
                No data to show
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-3">
        <ScrollableTable
          title={<>Top 10 Consumers <InfoTooltip text="Detailed list of subscribers with highest usage." /></>}
          data={tenants}
          columns={[
            { header: "Subscriber", accessor: "name", render: (v) => <span className="fw-bold text-dark">{v}</span> },
            { header: "Plan", accessor: "plan", render: (v) => <span className="badge bg-light text-dark border">{v}</span> },
            { header: "Executions", accessor: "executions", render: (v) => v.toLocaleString() },
            { header: "Storage Used", accessor: "storage" },
            { header: "% Execution Usage", accessor: "globalPct", render: (v) => <span className="text-secondary fw-bold">{v}</span> }
          ]}
          height={400}
        />
      </div>

      <ExpandedChartModal
        show={!!expandedChart}
        onClose={() => setExpandedChart(null)}
        {...expandedChart}
      />
    </div>
  );
}
